import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { Search, Loader2 } from 'lucide-react';
import { useHubspotSearch } from '@/hooks/api/useBilling';
import { useDebounce } from '@/hooks';
import { useToast } from '@/components/ui/use-toast';
import { Input } from '@/components/ui/input';
import type { HubspotCompanyHit, HubspotDealHit } from '@/api/billing';

interface HubspotEntitySearchInputProps {
  kind: 'company' | 'deal';
  onSelect: (id: string, label: string) => void;
  disabled?: boolean;
}

/**
 * Search-as-you-type picker for HubSpot companies/deals (TELV1-524 linking
 * assist). Backend requires >= 3 chars, so the query stays disabled below
 * that. Callers ALSO always render a plain "paste the id" input alongside
 * this — that's the fallback when HubSpot lookups aren't configured, so this
 * component only renders the search box, never the manual-id field.
 */
export function HubspotEntitySearchInput({
  kind,
  onSelect,
  disabled,
}: HubspotEntitySearchInputProps) {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const { toast } = useToast();

  const { data, isFetching, isError, error } = useHubspotSearch(kind, debouncedQuery);

  useEffect(() => {
    if (!isError) return;
    const status = error instanceof AxiosError ? error.response?.status : undefined;
    toast({
      title: status === 429 ? 'HubSpot rate limit' : 'Search failed',
      description:
        status === 429
          ? 'HubSpot rate limit — try again in a few seconds'
          : 'Failed to search HubSpot. You can paste the id manually instead.',
      variant: 'destructive',
    });
    // Only re-toast when the error identity changes, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, error]);

  const results = data?.results ?? [];
  const trimmed = debouncedQuery.trim();
  const showDropdown = trimmed.length >= 3 && (isFetching || results.length > 0);

  return (
    <div className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search HubSpot ${kind === 'company' ? 'companies' : 'deals'}...`}
          className="pl-9"
          disabled={disabled}
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
      </div>

      {showDropdown && (
        <div className="absolute z-10 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-card shadow-md">
          {results.length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">
              {isFetching ? 'Searching...' : 'No matches'}
            </div>
          ) : (
            results.map((item) => {
              const label =
                kind === 'company'
                  ? (item as HubspotCompanyHit).name || item.id
                  : (item as HubspotDealHit).dealname || item.id;
              const sub =
                kind === 'company'
                  ? (item as HubspotCompanyHit).domain
                  : (item as HubspotDealHit).dealstage;
              return (
                <button
                  key={item.id}
                  type="button"
                  className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-muted"
                  onClick={() => {
                    onSelect(item.id, label || item.id);
                    setQuery('');
                  }}
                >
                  <span className="font-medium text-foreground">{label}</span>
                  {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
